<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { LogOut, Settings, UserPlus, X, BookText } from "lucide-vue-next";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { SheetContent } from "~/shadcn/components/ui/sheet";
import { DrawerContent } from "~/shadcn/components/ui/drawer";
import ProfileDrawerSection from "~/components/profile/ProfileDrawerSection.vue";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { default as XIcon } from "/icon-X.svg?url";
import Instagram from "/icon-instagram.svg?url";
import Discord from "/icon-discord.svg?url";
import ProfileDrawerFooterButton from "~/components/profile/ProfileDrawerFooterButton.vue";

const { t, locale } = useI18n();
const router = useRouter();

const contentRef = ref(null);

const breakpoints = useBreakpoints(breakpointsTailwind);

const breakpointIsLgOrAbove = breakpoints.greaterOrEqual("lg");

const userStore = useStoreUserV2();

const handleReferralNavigation = () => {
  setTimeout(() => {
    userStore.closeProfileDrawer();
  }, 0);

  if (router.currentRoute.value.name !== "Referral") {
    router.push({ name: "Referral" });
  }
};

const social = [
  { alt: "X", url: "https://x.com/Fungiball_off", icon: XIcon },
  { alt: "Instagram", url: "https://www.instagram.com/fungiball.io", icon: Instagram },
  { alt: "Discord", url: "https://discord.gg/YJJw7RP33K", icon: Discord },
];

const handleLogout = () => {
  userStore.logout();
  userStore.closeProfileDrawer();
  router.push({ name: "Home" });
};

const openGuide = () => {
  if (locale.value === "fr") {
    window.open("https://guide.fungiball.io", "_blank");
  } else {
    window.open("https://guide.fungiball.io/en", "_blank");
  }
  userStore.closeProfileDrawer();
};

const credit = computed(() => {
  return userStore.creditList.activeCredits.length ? userStore.creditList.activeCredits[0] : null;
});
</script>

<template>
  <component
    :is="breakpointIsLgOrAbove ? SheetContent : DrawerContent"
    class="border-none outline-none p-0 bg-slate-950 text-white h-screen flex flex-col"
    hide-default-close-button
    ref="contentRef"
  >
    <div
      class="grow p-6 flex flex-col justify-start items-stretch gap-6 overflow-y-auto pb-12 lg:pb-6"
    >
      <ProfileDrawerSection>
        <ProfileDrawerUserInfo />
      </ProfileDrawerSection>

      <CreditCountDown v-if="credit" :credit-list="credit" />

      <div class="grid grid-cols-1 gap-1">
        <ProfileDrawerButton nav @click="handleReferralNavigation" new>
          <template #icon>
            <UserPlus class="w-4" />
          </template>
          {{ t("drawers.profile.defaultView.account.inviteAFriend") }}
        </ProfileDrawerButton>

        <ProfileDrawerButton nav @click="userStore.openProfileDrawer('settings')">
          <template #icon>
            <Settings class="w-4" />
          </template>
          {{ t("drawers.profile.defaultView.account.settings", "Settings") }}
        </ProfileDrawerButton>

        <ProfileDrawerButton nav @click="openGuide">
          <template #icon>
            <BookText class="w-4" />
          </template>
          Guide
        </ProfileDrawerButton>
      </div>

      <ProfileDrawerSection>
        <div class="bg-slate-900 p-4 py-2 mb-0.5 rounded-t-lg text-center text-xs text-slate-400">
          {{ t("drawers.profile.defaultView.account.followUs") }}
        </div>
        <ul class="flex flex-row items-center justify-center w-full join-us-btn">
          <li v-for="item in social" :key="item.alt" class="grow flex justify-center items-center">
            <a
              :href="item.url"
              target="_blank"
              class="w-full bg-slate-900 hover:bg-slate-800 flex justify-center items-center p-2 max-h-8 lg:max-h-10 xl:max-h-12"
            >
              <img :src="item.icon" :alt="item.alt" class="text-white w-4" />
            </a>
          </li>
        </ul>
      </ProfileDrawerSection>

      <div class="grow flex flex-col justify-end items-stretch">
        <ProfileDrawerButton nav @click="handleLogout">
          <template #icon>
            <LogOut class="w-4" />
          </template>
          {{ t("drawers.profile.defaultView.account.logOut") }}
        </ProfileDrawerButton>
      </div>
    </div>

    <ProfileDrawerFooterButton @click="userStore.closeProfileDrawer()">
      {{ t("drawers.close") }}
    </ProfileDrawerFooterButton>
  </component>
</template>

<style scoped lang="scss">
.join-us-btn {
  @apply gap-0.5;
  & li a {
    @apply h-20  !rounded-t-none;
  }
  & li:first-child a {
    @apply rounded-l-lg;
  }
  & li:last-child a {
    @apply rounded-r-lg;
  }
}
</style>
